import { Table, TableBody, TableContainer, Tooltip } from '@mui/material';
import {
    TableCell,
    TableHeader,
    TableRow,
    TableSortLabel,
    TableHeaderCell
} from '@/components/shared/styles/Table';
import {
    FONT_WEIGHT,
    TextMDSemiBold,
    TextSMRegular
} from '@/typography/Typography';
import { useTranslation } from 'react-i18next';
import { HistoryStatus } from '@/pages/history/HistoryStatus';
import { TipsHistory } from '@/services/PaymentsServiceV2';
import { useNavigate } from 'react-router-dom';
import { UnderlinedLink } from '@/components/shared/UnderlinedLink';
import { useContext, useState } from 'react';
import { UserContext } from '@/contexts/user/UserContext';
import { useFormatting } from '@/utils/formatting';
import { DATE_FORMAT } from '@/lib/constants';
import { useOrdering } from '@/lib/hooks/useOrdering';
import { EditTipsDialog } from './EditTipsDialog';
import { EditIcon } from '@/icons/EditIcon';
import { CheckIcon } from '@/icons/CheckIcon';
import { ErrorIcon } from '@/icons/ErrorIcon';
import { TrashIcon } from '@/icons/TrashIcon';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { useHandleResponse } from '@/lib/hooks/useHandleResponse';
import { ConfirmationOverlay } from '@/pages/company/disable/ConfirmationOverlay';
import { theme } from '@/lib/theme';

export type TipsLocationListProps = {
    items?: TipsHistory[];
    reloadTransactions: () => void;
    // Manual companies enter journals by hand, so they are the only ones that can delete
    // one. For integrated providers the next sync would just bring the journal back.
    allowJournalDelete?: boolean;
};

export const TipsLocationList = (props: TipsLocationListProps) => {
    const { t } = useTranslation();

    const { getBalanceLabel, toDecimal } = useFormatting();

    const { company } = useContext(UserContext);

    //const { company } = useContext(UserContext);
    const navigation = useNavigate();

    const { orderedItems, handleRequestSort, orderMap } = useOrdering(
        props?.items || [],
        'date',
        'desc'
    );

    const [editTip, setEditTip] = useState<TipsHistory | null>(null);
    const [deleteTip, setDeleteTip] = useState<TipsHistory | null>(null);

    const { deleteManualPosJournal } = usePaymentsMutations();
    useHandleResponse({
        error: deleteManualPosJournal.error,
        isSuccess: deleteManualPosJournal.isSuccess,
        successAlertMessage: t('tips.manual_journal_deleted'),
        isSuccessCallBack: () => setDeleteTip(null)
    });

    const getReportingStatusIcon = (tip: TipsHistory) => {
        if (
            tip.reportingStatus === 'Reported' ||
            tip.reportingStatus === 'FullyReported'
        ) {
            return <CheckIcon />;
        }
        if (tip.showError) {
            return <ErrorIcon />;
        }

        return null;
    };

    const getAmountReportedColor = (tip: TipsHistory) => {
        // Show red for negative amounts
        if (tip.amountSent < 0) {
            return 'error.main';
        }

        if (
            tip.isInCurrentPayrollPeriod &&
            tip.reportingStatus === 'NotReported' &&
            tip.amountSent === 0
        ) {
            return 'inherit';
        }

        // Show muted color for not reported or partially reported
        return (tip.reportingStatus === 'NotReported' ||
            tip.reportingStatus === 'PartiallyReported') &&
            tip.amountSent > 0
            ? 'text.muted'
            : 'inherit';
    };

    // The backend refuses to delete a journal whose tips reached the salary system, so the
    // action is locked here to spare the admin a failed round-trip.
    const isReported = (tip: TipsHistory) =>
        tip.reportingStatus !== 'NotReported';

    // A row groups every journal for the location/day but carries only the first journal's
    // id, and its reporting status reads NotReported when *any* one journal is. Deleting
    // from such a row would silently drop one journal of several, so it is blocked. Manual
    // entry allows one journal per location per day, so this only guards legacy days left
    // behind by a company that used to sync from a POS.
    const spansSeveralJournals = (tip: TipsHistory) =>
        (tip.posJournalAmounts?.length ?? 1) > 1;

    const deleteBlockedReason = (tip: TipsHistory) => {
        if (isReported(tip)) return t('tips.delete_journal_reported_blocked');
        if (spansSeveralJournals(tip))
            return t('tips.delete_journal_multiple_blocked');
        return '';
    };

    const getTotalColor = (tip: TipsHistory) => {
        // Show red for negative amounts
        if (tip.totalTips < 0) {
            return 'error.main';
        } else {
            return 'inherit';
        }
    };

    return (
        <>
            <TableContainer>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell
                                sx={{ fontWeight: 'bold', width: '180px' }}
                            >
                                <TableSortLabel
                                    direction={orderMap.date || 'asc'}
                                    onClick={() => handleRequestSort('date')}
                                >
                                    {t('Date')}
                                </TableSortLabel>
                            </TableHeaderCell>
                            <TableHeaderCell
                                sx={{ fontWeight: 'bold', width: '180px' }}
                            >
                                <TableSortLabel
                                    direction={
                                        orderMap.lastDistributedAt || 'asc'
                                    }
                                    onClick={() =>
                                        handleRequestSort('lastDistributedAt')
                                    }
                                >
                                    {t('Finalized Date')}
                                </TableSortLabel>
                            </TableHeaderCell>
                            <TableHeaderCell
                                sx={{ fontWeight: 'bold', width: '200px' }}
                            >
                                {t('Location')}
                            </TableHeaderCell>

                            <TableHeaderCell sx={{ fontWeight: 'bold' }}>
                                {t('Status')}
                            </TableHeaderCell>
                            <TableHeaderCell
                                align="right"
                                sx={{ fontWeight: 'bold' }}
                            >
                                <TableSortLabel
                                    align="right"
                                    direction={
                                        orderMap.totalEmployeeHours || 'asc'
                                    }
                                    onClick={() =>
                                        handleRequestSort('totalEmployeeHours')
                                    }
                                >
                                    {t('Approved hours')}
                                </TableSortLabel>
                            </TableHeaderCell>
                            <TableHeaderCell
                                align="right"
                                sx={{ fontWeight: 'bold' }}
                            >
                                <TableSortLabel
                                    align="right"
                                    direction={orderMap.totalTips || 'asc'}
                                    onClick={() =>
                                        handleRequestSort('totalTips')
                                    }
                                >
                                    {t('Total Tips')}
                                </TableSortLabel>
                            </TableHeaderCell>
                            <TableHeaderCell
                                align="center"
                                sx={{ fontWeight: 'bold' }}
                            >
                                {t('Reported')}
                            </TableHeaderCell>
                            <TableHeaderCell
                                align="right"
                                sx={{ fontWeight: 'bold', width: '150px' }}
                            >
                                {t('Amount reported')}
                            </TableHeaderCell>
                            <TableHeaderCell></TableHeaderCell>
                            {props.allowJournalDelete && (
                                <TableHeaderCell></TableHeaderCell>
                            )}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {orderedItems?.map((tip, index) => (
                            <TableRow
                                hoverEffect
                                key={JSON.stringify(tip) + index}
                                onClick={() => {
                                    const formattedDate = tip.date?.format(
                                        DATE_FORMAT.SERVER_DATE
                                    );
                                    navigation(
                                        `/tips-location?posLocationId=${tip.locationId}&date=${formattedDate}&status=${tip.status}`
                                    );
                                }}
                            >
                                <TableCell>
                                    <UnderlinedLink>
                                        {' '}
                                        {tip.date?.format(
                                            DATE_FORMAT.DAY_NAME_MONTH_NAME_DAY_YEAR
                                        )}
                                    </UnderlinedLink>
                                </TableCell>
                                <TableCell>
                                    {tip.lastDistributedAt
                                        ? tip.lastDistributedAt?.format(
                                              DATE_FORMAT.DAY_NAME_MONTH_NAME_DAY_YEAR
                                          )
                                        : '-'}
                                </TableCell>
                                <TableCell>{tip.locationName}</TableCell>
                                <TableCell
                                    sx={{ fontWeight: FONT_WEIGHT.MEDIUM }}
                                    component="th"
                                    scope="row"
                                >
                                    <HistoryStatus
                                        showDot={true}
                                        status={tip.status}
                                    />
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ fontWeight: FONT_WEIGHT.MEDIUM }}
                                >
                                    {toDecimal(tip.totalEmployeeHours, 2)}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        fontWeight: FONT_WEIGHT.MEDIUM,
                                        color: getTotalColor(tip)
                                    }}
                                >
                                    {getBalanceLabel(
                                        tip.totalTips,
                                        company?.currency
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    {getReportingStatusIcon(tip)}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        fontWeight: FONT_WEIGHT.MEDIUM,
                                        color: getAmountReportedColor(tip)
                                    }}
                                >
                                    {getBalanceLabel(
                                        tip.amountSent,
                                        company?.currency
                                    )}
                                </TableCell>
                                <TableCell
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setEditTip(tip);
                                    }}
                                >
                                    <EditIcon size={16} />
                                </TableCell>
                                {props.allowJournalDelete && (
                                    <TableCell
                                        data-testid={`delete-journal-${tip.posJournalId}`}
                                        sx={{
                                            cursor: deleteBlockedReason(tip)
                                                ? 'not-allowed'
                                                : 'pointer',
                                            opacity: deleteBlockedReason(tip)
                                                ? 0.4
                                                : 1
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (!deleteBlockedReason(tip))
                                                setDeleteTip(tip);
                                        }}
                                    >
                                        <Tooltip
                                            title={deleteBlockedReason(tip)}
                                        >
                                            <span>
                                                <TrashIcon size={16} />
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <EditTipsDialog
                editTip={editTip}
                onClose={() => {
                    setEditTip(null);
                }}
            />
            <ConfirmationOverlay
                danger
                open={!!deleteTip}
                loading={deleteManualPosJournal.isPending}
                onCancel={() => setDeleteTip(null)}
                onContinue={() => {
                    if (deleteTip)
                        deleteManualPosJournal.mutate(deleteTip.posJournalId);
                }}
                continueText={t('delete')}
                displayAreYouSureText={false}
                p1={
                    <TextMDSemiBold className="text-center">
                        {t('tips.delete_journal')}
                    </TextMDSemiBold>
                }
                p2={
                    <TextSMRegular
                        mb={2}
                        color={theme.palette.text.muted}
                        className="text-center"
                    >
                        {deleteTip
                            ? t('tips.delete_journal_confirm', {
                                  location: deleteTip.locationName,
                                  date: deleteTip.date?.format(
                                      DATE_FORMAT.DAY_MONTH_YEAR
                                  )
                              })
                            : ''}
                    </TextSMRegular>
                }
            />
        </>
    );
};
